import React from 'react';
import Image from 'next/image';
import QuizResetIcon from '../../quiz/quiz-reset-icon';
import QuizButton from '../quiz-button/quiz-button';
import QuizPurchaseIcon from '../../quiz/quiz-purchase-icon';
import QuizRefinanceIcon from '../../quiz/quiz-refinance-icon';
import QuizHomeEquityLoanIcon from '../../quiz/quiz-home-equity-icon';
import SegmentImpression from '~/app/components/segment-impression';
import useAnalytics from '~/app/lib/segment/useAnalytics';
import { ActionOption, ActionsEnum } from '../types';
import styles from '../combined-shopping-module.module.scss';
import FolderTabContainer from '../../folder-tab-container/folder-tab-container';

export interface IMortgageFlowProps {
  action: ActionOption;
  desktopSourceImage: string;
  mobileSourceImage: string;
  sourceAlt: string;
  handleOpenLink: (link: string) => void;
  resetQuiz: () => void;
}

export const MortgageFlow = ({
  action,
  desktopSourceImage,
  mobileSourceImage,
  sourceAlt,
  handleOpenLink,
  resetQuiz,
}: IMortgageFlowProps): JSX.Element => {
  const { trackSegmentTrackEvent } = useAnalytics();
  const purchaseLink =
    action.value === ActionsEnum.Personalize
      ? 'https://analyze.nerdwallet.com/mortgage/purchase/apply/property-type?affiliateName=NerdWallet&affiliateId=1333&subId1=organic&subId2=mortgage_refi_superbowl&subId3=homepage'
      : 'https://www.nerdwallet.com/best/mortgages/mortgage-lenders';

  const refinanceLink =
    action.value === ActionsEnum.Personalize
      ? 'https://analyze.nerdwallet.com/mortgage/refinance/apply/property-type?affiliateName=NerdWallet&affiliateId=1333&subId1=organic&subId2=mortgage_refi_superbowl&subId3=homepage'
      : 'https://www.nerdwallet.com/best/mortgages/refinance-lenders';

  const homeEquityLink =
    action.value === ActionsEnum.Personalize
      ? 'https://analyze.nerdwallet.com/home-equity?affiliateName=NerdWallet&affiliateId=1333&subId1=organic&subId2=mortgage_heloc_superbowl&subId3=homepage'
      : 'https://www.nerdwallet.com/best/mortgages/heloc-lenders';

  const handleCtaClick = (link: string, entityName: string) => {
    trackSegmentTrackEvent('Element Interaction', {
      entity_name: entityName,
    });
    handleOpenLink(link);
  };

  return (
    <SegmentImpression
      eventName="Element Impression"
      eventProps={{
        entity_name: `hp_shopping_quiz_${action.value}_mrt`,
      }}
    >
      {/* Desktop */}
      <div className={styles.desktopContainer}>
        <FolderTabContainer
          tabBackgroundColor="#e3faf2"
          contentBackgroundColor="#e3faf2"
          roundedCorners
          showBorderBottom
        >
          <div className={styles.combinedWrapper}>
            <div className={styles.alternativeCombinedForm}>
              <div className={styles.combinedHeader}>
                <span className={styles.formText}>
                  What kind of mortgage are you looking for?
                </span>
                <button className={styles.resetButton} onClick={resetQuiz}>
                  <QuizResetIcon className={styles.quizResetIcon} />
                </button>
              </div>
              <div className={styles.columnContainer}>
                <QuizButton
                  text="Purchase"
                  icon={() => <QuizPurchaseIcon width={40} />}
                  flexDirection="row"
                  onClick={() => {
                    handleCtaClick(
                      purchaseLink,
                      `hp_shopping_quiz_${action.value}_mrt_purchase_click`,
                    );
                  }}
                />
                <QuizButton
                  text="Refinance"
                  icon={() => <QuizRefinanceIcon width={40} />}
                  flexDirection="row"
                  onClick={() => {
                    handleCtaClick(
                      refinanceLink,
                      `hp_shopping_quiz_${action.value}_mrt_refi_click`,
                    );
                  }}
                />
                <QuizButton
                  text="Home Equity Loan"
                  icon={() => <QuizHomeEquityLoanIcon width={40} />}
                  flexDirection="row"
                  onClick={() => {
                    handleCtaClick(
                      homeEquityLink,
                      `hp_shopping_quiz_${action.value}_mrt_equity_click`,
                    );
                  }}
                />
              </div>
            </div>
            <div className={styles.desktopTabletImage}>
              <Image
                src={desktopSourceImage}
                alt={sourceAlt}
                width={482}
                height={424}
              />
            </div>
          </div>
        </FolderTabContainer>
      </div>
      {/* Mobile and Tablet */}
      <div className={styles.mobileTabletContainer}>
        <div className={styles.combinedWrapper}>
          <div className={styles.alternativeCombinedForm}>
            <div className={styles.combinedHeader}>
              <span className={styles.formText}>
                What kind of mortgage are you looking for?
              </span>
              <button className={styles.resetButton} onClick={resetQuiz}>
                <QuizResetIcon className={styles.quizResetIcon} />
              </button>
            </div>
            <div className={styles.columnContainer}>
              <QuizButton
                text="Purchase"
                icon={() => <QuizPurchaseIcon width={32} />}
                flexDirection="row"
                onClick={() => {
                  handleCtaClick(
                    purchaseLink,
                    `hp_shopping_quiz_${action.value}_mrt_purchase_click`,
                  );
                }}
              />
              <QuizButton
                text="Refinance"
                icon={() => <QuizRefinanceIcon width={32} />}
                flexDirection="row"
                onClick={() => {
                  handleCtaClick(
                    refinanceLink,
                    `hp_shopping_quiz_${action.value}_mrt_refi_click`,
                  );
                }}
              />
              <QuizButton
                text="Home Equity Loan"
                icon={() => <QuizHomeEquityLoanIcon width={32} />}
                flexDirection="row"
                onClick={() => {
                  handleCtaClick(
                    homeEquityLink,
                    `hp_shopping_quiz_${action.value}_mrt_equity_click`,
                  );
                }}
              />
            </div>
          </div>
          <div className={styles.desktopTabletImage}>
            <Image
              src={desktopSourceImage}
              alt={sourceAlt}
              width={482}
              height={424}
            />
          </div>
        </div>
        <div className={styles.mobileImage}>
          <Image
            src={mobileSourceImage}
            alt={sourceAlt}
            width={686}
            height={360}
          />
        </div>
      </div>
    </SegmentImpression>
  );
};
